import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Music } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { BackButton } from "@/components/BackButton";
import { FloatingThemeToggle } from "@/context/ThemeContext";
import { DeviceSelfReset } from "@/components/DeviceSelfReset";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [limited, setLimited] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLimited(false);
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      const isTeacher = res.user?.role === "teacher" || res.user?.role === "admin";
      nav(loc.state?.from || (isTeacher ? "/teacher" : "/dashboard"));
    } else { setError(res.error); setLimited(res.status === 403); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-secondary px-4">
      <FloatingThemeToggle />
      <div className="w-full max-w-md rounded-3xl border border-border bg-card p-8 shadow-xl shadow-amber-900/5">
        <BackButton testId="login-back-button" />
        <Link to="/" className="mt-2 flex items-center justify-center">
          <Logo height={54} className="dark:shadow-sm" />
        </Link>
        <h1 className="mt-6 text-center font-serif text-3xl text-foreground">Welcome back</h1>
        <p className="mt-2 text-center text-sm text-muted-foreground">Log in to keep your progress and scores.</p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <Label htmlFor="email" className="text-muted-foreground">Email</Label>
            <Input
              id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email" placeholder="you@example.com"
              className="mt-1 border-border bg-background"
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-muted-foreground">Password</Label>
              <Link to={`/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ""}`} data-testid="login-forgot-link" className="text-xs font-medium text-primary hover:underline">Forgot password?</Link>
            </div>
            <Input
              id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password" placeholder="••••••••"
              className="mt-1 border-border bg-background"
            />
          </div>
          {error && <p data-testid="login-error" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          {limited && <DeviceSelfReset email={email} password={password} onDone={() => { setLimited(false); setError(""); submit({ preventDefault: () => {} }); }} />}
          <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full rounded-full bg-primary py-6 text-foreground hover:bg-primary/90">
            {loading ? "Logging in…" : "Log in"}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          New here?{" "}
          <Link to="/register" data-testid="to-register" className="font-medium text-primary hover:underline">
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}
